import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

export default function HistoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [boothData, setBoothData] = useState(null);
  const [branchData, setBranchData] = useState(null);
  const [notification, setNotification] = useState(''); // State to store notification message
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) {
          // If no token, redirect to home
          throw new Error('User token not found');
        }

        const decodedToken = jwtDecode(token);
        if (decodedToken.exp < Date.now() / 1000) {
          // If token is expired, redirect to home
          throw new Error('Token expired');
        }

        const response = await axios.get(`https://fboothapi.azurewebsites.net/api/booking/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setBookingData(response.data);

        if (response.data.boothID) {
          const boothResponse = await axios.get(`https://fboothapi.azurewebsites.net/api/booth/${response.data.boothID}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          setBoothData(boothResponse.data);

          if (boothResponse.data.branchID) {
            const branchResponse = await axios.get(`https://fboothapi.azurewebsites.net/api/branch/${boothResponse.data.branchID}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            setBranchData(branchResponse.data);
          }
        }

        setLoading(false);
      } catch (err) {
        setError('Error fetching booking data');
        setLoading(false);
        navigate('/'); // Redirect to homepage if there is an error or the token is invalid/expired
      }
    };

    fetchBookingData();
  }, [id], [navigate]);

  const formatDate = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handlePaymentClick = () => {
    // Save the booking data in session storage
    sessionStorage.setItem('bookingData', JSON.stringify(bookingData));

    // Navigate to the payment page
    navigate('/payment');
  };

  const handleCancelBooking = async (bookingID) => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.post(
        `https://fboothapi.azurewebsites.net/api/booking/cancel?bookingID=${bookingID}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setBookingData((prevData) => ({
        ...prevData,
        bookingStatus: 'Cancelled',
      }));
      window.location.reload();

      // Set the notification message from the response
      setNotification(response.data.message);
    } catch (err) {
      console.error('Error cancelling booking', err);
      setNotification('Failed to cancel the booking. Please try again later.');
    } finally {
      setShowConfirmModal(false); // Close the modal after action
    }
  };

  const confirmCancelBooking = () => {
    setShowConfirmModal(true); // Show the confirmation modal
  };
  if (loading)
    return (
      <div className="flex items-center justify-center h-screen bg-blue-100">
        <div className="text-center">
          <div className="loader ease-linear rounded-full border-8 border-t-8 border-blue-200 h-64 w-64 mb-4"></div>
          <h2 className="text-center text-blue-600 text-xl font-semibold">
            Loading...
          </h2>
          <p className="text-blue-500">Please wait a second.</p>
        </div>
      </div>
    );

  if (error) return <div>{error}</div>;

  if (!bookingData || !boothData || !branchData) {
    return <div>Loading data...</div>;
  }

  return (
    <div className="bg-gray-100 flex flex-col justify-center items-center py-10">
       {showConfirmModal && (
  <div className="fixed inset-0 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg border border-black-100 shadow-lg max-w-md w-full">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Do you want to cancel this booking?</h2>
      {bookingData.paymentStatus === 'Paid' ? (
        <>
          <p>
            Refunds are only available if canceled 24 hours before: <span className="font-bold text-red-500">"{formatTime(bookingData.startTime)} - {formatDate(bookingData.startTime)}"</span>.
          </p>
          <p>
            Only <span className="font-bold text-red-500">50%</span> of the payment will be refunded.
          </p>
          <br/>
        </>
      ) : null}
      <div className="flex justify-end space-x-4">
        <button
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          onClick={() => setShowConfirmModal(false)}
        >
          No
        </button>
        <button
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          onClick={() => handleCancelBooking(bookingData.bookingID)}
        >
          Yes
        </button>
      </div>
    </div>
  </div>
)}
      <div className="m-12 bg-white p-8 rounded-lg shadow-md w-4/5 flex">
        {/* Right Section: Booth Image and Name */}
        <div className="flex-1 pr-8">
        <h3 className="text-4xl font-bold text-gray-800 mb-5">
            {boothData.boothName}
          </h3>
          <div className="flex justify-center mb-8">
            <img
              src={boothData?.boothPhotos[0]?.boothPhotoUrl || 'https://via.placeholder.com/400x300'}
              alt="Booth"
              className="rounded-lg w-full h-auto"
            />
          </div>
        </div>

        {/* Left Section: Booking Details */}
        <div className="flex-1 pl-4 border-l border-gray-300">
          <div className="pt-4 mb-4">
            <p className="mt-4 text-base text-gray-600">
              Booking Status:
              <span className='text-base font-bold text-gray-600'> {bookingData.bookingStatus === 'Cancelled' ? (
                <>
                  <span className="font-semibold">{bookingData.bookingStatus}</span>
                  {bookingData.cancelledDate && (
                    <>
                      {' '}
                      on {formatDate(bookingData.cancelledDate)}
                    </>
                  )}
                </>
              ) : (
                bookingData.bookingStatus
              )}
              </span>
            </p>
          </div>
          {bookingData.bookingStatus === 'Canceled' && bookingData.refundedAmount !== 0 && (
            <div className="bg-yellow-100 p-4 rounded-lg mb-4">
              <p className="text-sm font-semibold text-yellow-800">
                Refund Amount: {bookingData.refundedAmount.toLocaleString()} VND
              </p>
            </div>
          )}

          <div className="flex items-center justify-center py-8">
            <div
              className={`p-4 rounded-full ${
                bookingData.bookingStatus === 'Canceled'
                  ? 'bg-yellow-100'
                  : bookingData.paymentStatus === 'Paid' || bookingData.paymentStatus === 'Refunded'
                  ? 'bg-green-100'
                  : bookingData.paymentStatus === 'Processing' || bookingData.paymentStatus === 'PendingPayExtra' || bookingData.paymentStatus === 'PendingRefund'
                  ? 'bg-yellow-100'
                  : 'bg-red-100'
              }`}
            >
              {bookingData.bookingStatus === 'Canceled' ? (
                <svg
                  className="text-yellow-500 w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v4m0 4h.01"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.5a7.5 7.5 0 11-7.5 7.5 7.5 7.5 0 017.5-7.5z"
                  />
                </svg>
              ) : bookingData.paymentStatus === 'Paid' || bookingData.paymentStatus === 'Refunded' ? (
                <svg
                  className="text-green-500 w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              ) : bookingData.paymentStatus === 'Processing' || bookingData.paymentStatus === 'PendingPayExtra' || bookingData.paymentStatus === 'PendingRefund' ? (
                <svg
                  className="text-yellow-500 w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v4m0 4h.01"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.5a7.5 7.5 0 11-7.5 7.5 7.5 7.5 0 017.5-7.5z"
                  />
                </svg>
              ) : (
                <svg
                  className="text-red-500 w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              )}
            </div>
            <h2
              className={`text-xl font-semibold ml-4 ${
                bookingData.bookingStatus === 'Canceled'
                  ? 'text-yellow-800'
                  : bookingData.paymentStatus === 'Paid' || bookingData.paymentStatus === 'Refunded'
                  ? 'text-gray-800'
                  : bookingData.paymentStatus === 'Processing' || bookingData.paymentStatus === 'PendingPayExtra' || bookingData.paymentStatus === 'PendingRefund'
                  ? 'text-yellow-800'
                  : 'text-red-800'
              }`}
            >
              {
                bookingData.bookingStatus === 'Canceled'
                  ? 'Booking was canceled'
                  : bookingData.paymentStatus === 'Paid' && bookingData.bookingStatus !== 'Canceled'
                  ? 'Your booking is now confirmed!'
                  : bookingData.bookingStatus === 'Canceled'
                  ? 'Canceled successful'
                  : bookingData.paymentStatus === 'Processing' || bookingData.paymentStatus === 'PendingPayExtra'
                  ? 'Please complete your payment'
                  : bookingData.paymentStatus === 'PendingRefund'
                  ? 'Awaiting refund'
                  : bookingData.paymentStatus === 'Refunded'
                  ? 'Refund successful'
                  : 'Your booking has failed!'
              }
            </h2>
          </div>

          {/* Trip Details */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-lg font-semibold text-gray-800 mb-2">
              Your booking starts {formatDate(bookingData.startTime)}
            </p>
            <div className="flex text-gray-600 mb-4">
              <div className="flex items-center mr-8">
                <svg
                  className="w-5 h-5 mr-2 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-6 8h6m-6 4h6m-9 4h12a2 2 0 002-2V7a2 2 0 00-2-2h-3.5L14 3H10L9.5 5H6a2 2 0 00-2 2v10a2 2 0 002 2z"
                  ></path>
                </svg>
                <p>
                  <span className="font-semibold">Check-in:</span>{' '}
                  {formatDate(bookingData.startTime)}, from{' '}
                  {formatTime(bookingData.startTime)}
                </p>
              </div>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-6 8h6m-6 4h6m-9 4h12a2 2 0 002-2V7a2 2 0 00-2-2h-3.5L14 3H10L9.5 5H6a2 2 0 00-2 2v10a2 2 0 002 2z"
                  ></path>
                </svg>
                <p>
                  <span className="font-semibold">Check-out:</span>{' '}
                  {formatDate(bookingData.endTime)}, until{' '}
                  {formatTime(bookingData.endTime)}
                </p>
              </div>
            </div>

            {/* Contact Details */}
            <div className="text-gray-600 mb-4">
              <p className="mb-2">
                <span className="font-semibold">Branch:</span>{' '}
                {branchData.branchName}
              </p>
              <p className="mb-2">
                <span className="font-semibold">Address:</span>{' '}
                {branchData.address}, {branchData.town}, {branchData.city}
              </p>

              <p>Payment status:
  <span
    className={`ml-2 font-bold ${
      bookingData.paymentStatus === 'PendingPayExtra' || bookingData.paymentStatus === 'PendingRefund' || bookingData.paymentStatus === 'Processing'
        ? 'text-yellow-500'
        : bookingData.paymentStatus === 'Refunded' || bookingData.paymentStatus === 'Paid'
        ? 'text-green-500'
        : 'text-red-500'
    }`}
  >
    {bookingData.paymentStatus}
  </span>
</p>
{bookingData.paymentStatus === 'Paid' && (
  <p className="mb-2">
    Code Validate:
    <span className="font-semibold">{bookingData.validateCode}</span>
    
  </p>
)}


            </div>
            <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Booking Slots</h3>
            <table className="w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-blue-100">
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Slot Date</th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Start Time</th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">End Time</th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Price (VND)</th>
                </tr>
              </thead>
              <tbody>
                {bookingData.bookingSlots.map((slot, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{formatDate(slot.bookingDate)}</td>
                    <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{slot.slotStartTime}</td>
                    <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{slot.slotEndTime}</td>
                    <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{slot.price.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

            {/* Service Details in Table */}
            <div className="border-t border-gray-200 pt-4">
  <h3 className="text-lg font-semibold text-gray-800 mb-4">Booking Services</h3>
  <div className='rounded-lg'>
    {bookingData.bookingServices.length === 0 ? (
      <p className="text-sm text-gray-600">You have not added any services yet</p>
    ) : (
      <table className="w-full table-auto border-collapse border border-gray-300">
        <thead>
          <tr className="bg-blue-100">
            <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Service Name</th>
            <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Description</th>
            <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Quantity</th>
            <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Unit Price (VND)</th>
            <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-600">Subtotal (VND)</th>
          </tr>
        </thead>
        <tbody>
          {bookingData.bookingServices.map((service, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{service.service.serviceName}</td>
              <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{service.service.serviceDescription}</td>
              <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{service.quantity}</td>
              <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{service.price.toLocaleString()}</td>
              <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">{service.subTotal.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
</div>

            {/* Price Details */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center text-gray-800 mb-2">
                <p className="font-semibold text-lg">Total Price</p>
                <p className="font-semibold text-lg">
                  {bookingData.totalPrice?.toLocaleString() || 'N/A'} VND{' '}
                  
                </p>
              </div>
              <div className="flex justify-between items-center text-gray-800 mb-2">
                <p className="font-semibold text-green-500 text-lg">Paid</p>
                <p className="font-semibold  text-green-500 text-lg">
                  {bookingData.paidAmount?.toLocaleString() || 'N/A'} VND{' '}
                  
                </p>
              </div>
                 {/* Display the amount that needs to be paid if status is PendingPayExtra */}
                 {bookingData.paymentStatus === 'PendingPayExtra' ||  bookingData.paymentStatus === 'Processing' && (
                <div className="flex justify-between items-center text-gray-800 mb-4">
                <p className="font-semibold text-lg text-red-500"> Need Pay</p>
                <p className="font-semibold text-lg text-red-500">
                {(bookingData.totalPrice - bookingData.paidAmount).toLocaleString()} VND 
                </p>
              </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                className="flex-1 bg-blue-500 text-white py-3 rounded-lg shadow hover:bg-blue-600"
                onClick={() => navigate('/history')}
              >
                View History
              </button>
              <button
                className="flex-1 border border-blue-500 text-blue-500 py-3 rounded-lg shadow hover:bg-blue-50"
                onClick={() => navigate('/')}
              >
                Homepage
              </button>
              {bookingData.bookingStatus === 'PendingPayment' && 
                (bookingData.paymentStatus === 'Processing' || bookingData.paymentStatus === 'Fail') && (
                <button
                  className="flex-1 bg-yellow-500 text-white py-3 rounded-lg shadow hover:bg-yellow-600"
                  onClick={handlePaymentClick}
                >
                  Pay
                </button>
              )}

              {bookingData.bookingStatus === 'ExtraService' && bookingData.paymentStatus === 'PendingPayExtra' && (
                <button
                  className="flex-1 bg-yellow-500 text-white py-3 rounded-lg shadow hover:bg-yellow-600"
                  onClick={handlePaymentClick}
                >
                  Pay Extra
                </button>
              )}
              {(bookingData.bookingStatus === 'PendingPayment' ||
                bookingData.bookingStatus === 'PendingChecking') && (
                <button
                  onClick={confirmCancelBooking}
                  className="flex items-center justify-center bg-white py-2 px-4 border border-red-500 rounded-lg shadow-sm text-sm font-medium text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Cancel Booking
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
